import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import Header from './components/Header';
import Footer from './components/Footer';
import AnimalCard from './components/AnimalCard';
import GenerateButton from './components/GenerateButton';
import useAnimal from './hooks/useAnimal';
import animalApi from './api/animalApi';

// Animal educational articles data
const animalArticles = [
  {
    id: 1,
    title: "The Origins and Domestication History of Dogs",
    content: "Dogs were one of the first animals domesticated by humans, dating back approximately 15,000 years. Scientists believe dogs evolved from wolves through domestication. Through selective breeding over millennia, humans have created the diverse dog breeds we know today. From working dogs to companion animals, dogs play multiple roles in human society and are often called man's best friend.",
    imageUrl: "https://images.unsplash.com/photo-1543466835-00a7907e9de1?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=1074&q=80"
  },
  {
    id: 2,
    title: "Cat Behavior and Habits Explained",
    content: "Cats are highly independent animals that retain many habits from their wild ancestors. Feline behaviors like purring, kneading, and scratching serve both emotional expression and physiological functions. Cat purring, beyond indicating contentment, has healing properties - the vibration frequency promotes bone and tissue healing. This self-healing mechanism showcases their remarkable evolutionary adaptations.",
    imageUrl: "https://images.unsplash.com/photo-1514888286974-6c03e2ca1dba?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=1073&q=80"
  },
  {
    id: 3,
    title: "Elephant Social Structures and Intelligence",
    content: "Elephants are the largest land animals on Earth and among the most intelligent. They maintain complex social structures led by matriarch-centered family groups. Elephants possess remarkable memory and emotional capacity - they mourn their dead companions and may even return to sites where relatives died for a form of memorial. This behavior reflects their highly developed social cognition capabilities.",
    imageUrl: "https://images.unsplash.com/photo-1557050543-4d5f4e07ef46?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=1032&q=80"
  }
];

/**
 * Main application component
 * Integrates all subcomponents and manages application state
 */
const App = () => {
  // Use custom hook to get animal data and states
  const { 
    animal, 
    loading, 
    error, 
    showAnimal, 
    fetchRandomAnimal,
    setAnimal,
    setLoading,
    setError,
    setShowAnimal
  } = useAnimal();
  
  // State for dog image section
  const [dogImage, setDogImage] = useState(null);
  const [dogBreed, setDogBreed] = useState("");
  const [loadingDog, setLoadingDog] = useState(false);
  const [dogError, setDogError] = useState(null);
  
  // State for bird image section
  const [birdImage, setBirdImage] = useState(null);
  const [birdName, setBirdName] = useState("");
  const [birdFact, setBirdFact] = useState("");
  const [loadingBird, setLoadingBird] = useState(false);
  const [birdError, setBirdError] = useState(null);
  
  // State for koala image section
  const [koalaImage, setKoalaImage] = useState(null);
  const [koalaFact, setKoalaFact] = useState("");
  const [loadingKoala, setLoadingKoala] = useState(false);
  const [koalaError, setKoalaError] = useState(null);

  // Automatically fetch a random animal when component first loads
  useEffect(() => {
    fetchRandomAnimal();
  }, [fetchRandomAnimal]);
  
  // Handler to get random dog images
  const handleGetRandomDog = async () => {
    setLoadingDog(true);
    setDogError(null);
    
    try {
      // Get random dog image
      const dogData = await animalApi.getRandomDog();
      
      // Update dog image state (not the animal card)
      setDogImage(dogData.image);
      setDogBreed(dogData.name.replace("Dog (", "").replace(")", ""));
    } catch (err) {
      setDogError(`Failed to get random dog image: ${err.message}`);
      console.error(err);
    } finally {
      setLoadingDog(false);
    }
  };
  
  // Handler to get random bird images
  const handleGetRandomBird = async () => {
    setLoadingBird(true);
    setBirdError(null);
    
    try {
      // Get random bird data
      const birdData = await animalApi.getRandomBird();
      
      // Update bird image state
      setBirdImage(birdData.image);
      setBirdName(birdData.name);
      setBirdFact(birdData.fact);
    } catch (err) {
      setBirdError(`Failed to get random bird image: ${err.message}`);
      console.error(err);
    } finally {
      setLoadingBird(false);
    }
  };
  
  // Handler to get random koala images
  const handleGetRandomKoala = async () => {
    setLoadingKoala(true);
    setKoalaError(null);
    
    try {
      // Get random koala data
      const koalaData = await animalApi.getRandomKoala();
      
      // Update koala image state
      setKoalaImage(koalaData.image);
      setKoalaFact(koalaData.fact);
    } catch (err) {
      setKoalaError(`Failed to get random koala image: ${err.message}`);
      console.error(err);
    } finally {
      setLoadingKoala(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-b from-background-light to-background">
      {/* Header */}
      <Header />
      
      <main className="flex-grow container mx-auto px-4 py-6">
        {/* Main content area */}
        <section className="max-w-4xl mx-auto">
          {/* Random Animal Generator Section */}
          <div className="mb-16">
            <h2 className="text-2xl font-bold mb-6 text-center">Random Animal Generator</h2>
            <GenerateButton 
              onGenerate={fetchRandomAnimal}
              isLoading={loading}
              text="Generate Random Animal"
            />
            
            {/* Error message */}
            {error && (
              <motion.div 
                className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-lg mb-6"
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
              >
                <p className="mb-1 font-medium">Problem getting animal data:</p>
                <p>{error}</p>
                <p className="mt-2 text-sm">Using fallback data source...</p>
              </motion.div>
            )}
            
            {/* Animal card */}
            <AnimalCard 
              animal={animal} 
              isVisible={showAnimal}
            />
            
            {/* Welcome card - shown when first visiting */}
            {!animal && !loading && !error && (
              <motion.div 
                className="text-center p-8 bg-white rounded-xl shadow-md"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.5 }}
              >
                <span className="text-4xl mb-4 block">🐾</span>
                <h2 className="text-xl font-bold mb-2">Welcome to Random Animal Generator!</h2>
                <p className="text-gray-600">
                  Click the buttons above to explore amazing creatures from the animal kingdom.
                </p>
              </motion.div>
            )}
          </div>
          
          {/* Three animal images section */}
          <div className="mb-16">
            <h2 className="text-2xl font-bold mb-8 text-center">Explore Different Animals</h2>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {/* Dog Image Generator */}
              <div className="bg-white rounded-xl shadow-md p-4">
                <h3 className="text-xl font-bold mb-4 text-center">Dog Images</h3>
                
                {/* Dog Image Display Area */}
                <motion.div 
                  className="mb-6 w-full overflow-hidden rounded-xl bg-gray-100"
                  initial={{ opacity: 1 }}
                  animate={{ opacity: dogImage ? 1 : 0.5 }}
                  transition={{ duration: 0.3 }}
                  style={{ height: "200px" }}
                >
                  {dogImage ? (
                    <div className="relative h-full">
                      <motion.img 
                        src={dogImage} 
                        alt={`A ${dogBreed} dog`}
                        className="w-full h-full object-cover"
                        key={dogImage} // This forces re-render when image changes
                        initial={{ opacity: 0, scale: 0.95 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ duration: 0.5 }}
                        loading="lazy"
                      />
                      <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-3">
                        <motion.p 
                          className="text-white font-medium text-sm"
                          initial={{ y: 20, opacity: 0 }}
                          animate={{ y: 0, opacity: 1 }}
                          transition={{ delay: 0.3 }}
                        >
                          {dogBreed}
                        </motion.p>
                      </div>
                      
                      {/* Social Media Share Overlay */}
                      <div className="absolute top-2 right-2 bg-white/80 backdrop-blur-sm rounded-lg p-1.5 shadow-lg">
                        <motion.button
                          className="text-xs font-medium text-gray-700 flex items-center"
                          initial={{ scale: 0.9, opacity: 0 }}
                          animate={{ scale: 1, opacity: 1 }}
                          transition={{ delay: 0.5 }}
                          whileHover={{ scale: 1.05 }}
                          whileTap={{ scale: 0.95 }}
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5 mr-1" viewBox="0 0 20 20" fill="currentColor">
                            <path d="M15 8a3 3 0 10-2.977-2.63l-4.94 2.47a3 3 0 100 4.319l4.94 2.47a3 3 0 10.895-1.789l-4.94-2.47a3.027 3.027 0 000-.74l4.94-2.47C13.456 7.68 14.19 8 15 8z" />
                          </svg>
                          Share
                        </motion.button>
                      </div>
                    </div>
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <p className="text-gray-500 text-sm text-center px-4">Click the button below to generate a dog image</p>
                    </div>
                  )}
                </motion.div>
                
                {/* Dog Generator Button */}
                <div className="text-center">
                  <motion.button
                    className="btn-secondary flex items-center justify-center mx-auto"
                    onClick={handleGetRandomDog}
                    disabled={loadingDog}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    {loadingDog ? (
                      <>
                        <svg className="animate-spin -ml-1 mr-2 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Loading...
                      </>
                    ) : (
                      <>
                        <span className="text-xl mr-2" aria-hidden="true">🐕</span>
                        Get Dog Image
                      </>
                    )}
                  </motion.button>
                </div>
                
                {/* Social Media Share Buttons - Only shown when a dog image is present */}
                {dogImage && (
                  <motion.div 
                    className="mt-4 flex justify-center gap-3"
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.2 }}
                  >
                    <h4 className="text-sm font-medium text-gray-700 mr-2 self-center">Share on:</h4>
                    
                    {/* Reddit Share Button */}
                    <motion.a
                      href={`https://www.reddit.com/submit?url=${encodeURIComponent(dogImage)}&title=${encodeURIComponent(`Check out this adorable ${dogBreed}!`)}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="bg-[#FF4500] text-white rounded-full p-2 flex items-center justify-center shadow-md hover:shadow-lg"
                      whileHover={{ scale: 1.1 }}
                      whileTap={{ scale: 0.9 }}
                      title="Share on Reddit"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M12 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0zm5.01 4.744c.688 0 1.25.561 1.25 1.249a1.25 1.25 0 0 1-2.498.056l-2.597-.547-.8 3.747c1.824.07 3.48.632 4.674 1.488.308-.309.73-.491 1.207-.491.968 0 1.754.786 1.754 1.754 0 .716-.435 1.333-1.01 1.614a3.111 3.111 0 0 1 .042.52c0 2.694-3.13 4.87-7.004 4.87-3.874 0-7.004-2.176-7.004-4.87 0-.183.015-.366.043-.534A1.748 1.748 0 0 1 4.028 12c0-.968.786-1.754 1.754-1.754.463 0 .898.196 1.207.49 1.207-.883 2.878-1.43 4.744-1.487l.885-4.182a.342.342 0 0 1 .14-.197.35.35 0 0 1 .238-.042l2.906.617a1.214 1.214 0 0 1 1.108-.701zM9.25 12C8.561 12 8 12.562 8 13.25c0 .687.561 1.248 1.25 1.248.687 0 1.248-.561 1.248-1.249 0-.688-.561-1.249-1.249-1.249zm5.5 0c-.687 0-1.248.561-1.248 1.25 0 .687.561 1.248 1.249 1.248.688 0 1.249-.561 1.249-1.249 0-.687-.562-1.249-1.25-1.249zm-5.466 3.99a.327.327 0 0 0-.231.094.33.33 0 0 0 0 .463c.842.842 2.484.913 2.961.913.477 0 2.105-.056 2.961-.913a.361.361 0 0 0 .029-.463.33.33 0 0 0-.464 0c-.547.533-1.684.73-2.512.73-.828 0-1.979-.196-2.512-.73a.326.326 0 0 0-.232-.095z" />
                      </svg>
                    </motion.a>
                    
                    {/* X (Twitter) Share Button */}
                    <motion.a
                      href={`https://twitter.com/intent/tweet?url=${encodeURIComponent(dogImage)}&text=${encodeURIComponent(`Check out this adorable ${dogBreed}!`)}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="bg-black text-white rounded-full p-2 flex items-center justify-center shadow-md hover:shadow-lg"
                      whileHover={{ scale: 1.1 }}
                      whileTap={{ scale: 0.9 }}
                      title="Share on X (Twitter)"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
                      </svg>
                    </motion.a>
                    
                    {/* Facebook Share Button */}
                    <motion.a
                      href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(dogImage)}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="bg-[#1877F2] text-white rounded-full p-2 flex items-center justify-center shadow-md hover:shadow-lg"
                      whileHover={{ scale: 1.1 }}
                      whileTap={{ scale: 0.9 }}
                      title="Share on Facebook"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M9.101 23.691v-7.98H6.627v-3.667h2.474v-1.58c0-4.085 1.848-5.978 5.858-5.978.401 0 .955.042 1.468.103a8.68 8.68 0 0 1 1.141.195v3.325a8.623 8.623 0 0 0-.653-.036 26.805 26.805 0 0 0-.733-.009c-.707 0-1.259.096-1.675.309a1.686 1.686 0 0 0-.679.622c-.258.42-.374.995-.374 1.752v1.297h3.919l-.386 2.103-.287 1.564h-3.246v8.245C19.396 23.238 24 18.179 24 12.044c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.628 3.874 10.35 9.101 11.647Z" />
                      </svg>
                    </motion.a>
                    
                    {/* Instagram Share Button (Note: Instagram doesn't support direct URL sharing via web, this is for UI consistency) */}
                    <motion.a
                      href="#"
                      onClick={(e) => {
                        e.preventDefault();
                        alert("Instagram doesn't support direct sharing. Please save the image and upload it to your Instagram account.");
                      }}
                      className="bg-gradient-to-tr from-[#FFDC80] via-[#F77737] via-[#C13584] to-[#833AB4] text-white rounded-full p-2 flex items-center justify-center shadow-md hover:shadow-lg"
                      whileHover={{ scale: 1.1 }}
                      whileTap={{ scale: 0.9 }}
                      title="Share on Instagram (Save image first)"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z" />
                      </svg>
                    </motion.a>
                  </motion.div>
                )}
                
                {/* Dog Error message */}
                {dogError && (
                  <motion.div 
                    className="bg-red-50 border border-red-200 text-red-600 px-3 py-2 text-sm rounded-lg mt-3"
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                  >
                    <p>{dogError}</p>
                  </motion.div>
                )}
              </div>
              
              {/* Bird Image Generator */}
              <div className="bg-white rounded-xl shadow-md p-4">
                <h3 className="text-xl font-bold mb-4 text-center">Bird Images</h3>
                
                {/* Bird Image Display Area */}
                <motion.div 
                  className="mb-6 w-full overflow-hidden rounded-xl bg-gray-100"
                  initial={{ opacity: 1 }}
                  animate={{ opacity: birdImage ? 1 : 0.5 }}
                  transition={{ duration: 0.3 }}
                  style={{ height: "200px" }}
                >
                  {birdImage ? (
                    <div className="relative h-full">
                      <motion.img 
                        src={birdImage} 
                        alt={`A beautiful bird`}
                        className="w-full h-full object-cover"
                        key={birdImage} // This forces re-render when image changes
                        initial={{ opacity: 0, scale: 0.95 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ duration: 0.5 }}
                        loading="lazy"
                      />
                      <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-3">
                        <motion.p 
                          className="text-white font-medium text-sm"
                          initial={{ y: 20, opacity: 0 }}
                          animate={{ y: 0, opacity: 1 }}
                          transition={{ delay: 0.3 }}
                        >
                          Birds
                        </motion.p>
                      </div>
                    </div>
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <p className="text-gray-500 text-sm text-center px-4">Click the button below to generate a bird image</p>
                    </div>
                  )}
                </motion.div>
                
                {/* Bird Generator Button */}
                <div className="text-center">
                  <motion.button
                    className="btn-secondary flex items-center justify-center mx-auto"
                    onClick={handleGetRandomBird}
                    disabled={loadingBird}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    {loadingBird ? (
                      <>
                        <svg className="animate-spin -ml-1 mr-2 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Loading...
                      </>
                    ) : (
                      <>
                        <span className="text-xl mr-2" aria-hidden="true">🦜</span>
                        Get Bird Image
                      </>
                    )}
                  </motion.button>
                </div>
                
                {/* Bird Error message */}
                {birdError && (
                  <motion.div 
                    className="bg-red-50 border border-red-200 text-red-600 px-3 py-2 text-sm rounded-lg mt-3"
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                  >
                    <p>{birdError}</p>
                  </motion.div>
                )}
              </div>
              
              {/* Koala Image Generator */}
              <div className="bg-white rounded-xl shadow-md p-4">
                <h3 className="text-xl font-bold mb-4 text-center">Koala Images</h3>
                
                {/* Koala Image Display Area */}
                <motion.div 
                  className="mb-6 w-full overflow-hidden rounded-xl bg-gray-100"
                  initial={{ opacity: 1 }}
                  animate={{ opacity: koalaImage ? 1 : 0.5 }}
                  transition={{ duration: 0.3 }}
                  style={{ height: "200px" }}
                >
                  {koalaImage ? (
                    <div className="relative h-full">
                      <motion.img 
                        src={koalaImage} 
                        alt="A cute koala"
                        className="w-full h-full object-cover"
                        key={koalaImage} // This forces re-render when image changes
                        initial={{ opacity: 0, scale: 0.95 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ duration: 0.5 }}
                        loading="lazy"
                      />
                      <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-3">
                        <motion.p 
                          className="text-white font-medium text-sm"
                          initial={{ y: 20, opacity: 0 }}
                          animate={{ y: 0, opacity: 1 }}
                          transition={{ delay: 0.3 }}
                        >
                          考拉
                        </motion.p>
                      </div>
                    </div>
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <p className="text-gray-500 text-sm text-center px-4">Click the button below to generate a koala image</p>
                    </div>
                  )}
                </motion.div>
                
                {/* Koala Generator Button */}
                <div className="text-center">
                  <motion.button
                    className="btn-secondary flex items-center justify-center mx-auto"
                    onClick={handleGetRandomKoala}
                    disabled={loadingKoala}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    {loadingKoala ? (
                      <>
                        <svg className="animate-spin -ml-1 mr-2 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Loading...
                      </>
                    ) : (
                      <>
                        <span className="text-xl mr-2" aria-hidden="true">🐨</span>
                        Get Koala Image
                      </>
                    )}
                  </motion.button>
                </div>
                
                {/* Koala Error message */}
                {koalaError && (
                  <motion.div 
                    className="bg-red-50 border border-red-200 text-red-600 px-3 py-2 text-sm rounded-lg mt-3"
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                  >
                    <p>{koalaError}</p>
                  </motion.div>
                )}
              </div>
            </div>
            
            {/* Facts display area */}
            <div className="mt-8 flex justify-center">
              {(birdFact || koalaFact) && (
                <motion.div 
                  className="bg-white rounded-xl shadow-md p-4 max-w-2xl"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5 }}
                >
                  <h3 className="text-lg font-medium mb-2">Animal Facts:</h3>
                  {birdFact && (
                    <motion.p 
                      className="text-gray-600 mb-2"
                      key={birdFact}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                    >
                      <span className="font-medium">🦜 Bird:</span> {birdFact}
                    </motion.p>
                  )}
                  {koalaFact && (
                    <motion.p 
                      className="text-gray-600"
                      key={koalaFact}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                    >
                      <span className="font-medium">🐨 Koala:</span> {koalaFact}
                    </motion.p>
                  )}
                </motion.div>
              )}
            </div>
          </div>
        </section>
        
        {/* User Feedback Section */}
        <section className="max-w-4xl mx-auto mt-16 mb-16">
          <motion.div
            className="bg-gradient-to-r from-primary-light to-blue-50 rounded-xl shadow-lg overflow-hidden"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true, margin: "-100px" }}
          >
            <div className="md:flex">
              <div className="md:w-2/5 bg-primary p-8 text-white flex flex-col justify-center">
                <h2 className="text-2xl font-bold mb-4">Help Us Improve!</h2>
                <p className="mb-4">We value your feedback and ideas on how to make our Random Animal Generator even better.</p>
                <ul className="space-y-2">
                  <li className="flex items-start">
                    <svg className="h-5 w-5 mr-2 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    <span>Share feature suggestions</span>
                  </li>
                  <li className="flex items-start">
                    <svg className="h-5 w-5 mr-2 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    <span>Report issues or bugs</span>
                  </li>
                  <li className="flex items-start">
                    <svg className="h-5 w-5 mr-2 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    <span>Tell us what animals you'd like to see</span>
                  </li>
                </ul>
              </div>
              
              <div className="md:w-3/5 p-8 bg-white">
                <form 
                  className="space-y-4" 
                  action="https://formsubmit.co/liuqi88048@gmail.com" 
                  method="POST"
                  onSubmit={(e) => {
                    // 简单表单验证
                    const feedback = e.target.feedback.value;
                    if (!feedback.trim()) {
                      e.preventDefault();
                      alert("Please provide your feedback before submitting.");
                    }
                  }}
                >
                  {/* FormSubmit配置 */}
                  <input type="hidden" name="_subject" value="New Feedback from Random Animal Generator" />
                  <input type="hidden" name="_captcha" value="false" />
                  <input type="hidden" name="_template" value="table" />
                  <input type="hidden" name="_next" value={window.location.href} />
                  
                  {/* 防垃圾邮件的蜜罐字段 - 正常用户看不到这个字段，但机器人会尝试填写 */}
                  <input type="text" name="_honey" style={{display: 'none'}} />
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">Your Name</label>
                      <input
                        type="text"
                        id="name"
                        name="name"
                        className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-primary focus:border-primary"
                        placeholder="John Doe"
                      />
                    </div>
                    <div>
                      <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">Email (optional)</label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-primary focus:border-primary"
                        placeholder="your@email.com"
                      />
                    </div>
                  </div>
                  
                  <div>
                    <label htmlFor="feedbackType" className="block text-sm font-medium text-gray-700 mb-1">Feedback Type</label>
                    <select
                      id="feedbackType"
                      name="feedbackType"
                      className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-primary focus:border-primary"
                    >
                      <option value="suggestion">Feature Suggestion</option>
                      <option value="bug">Bug Report</option>
                      <option value="content">Content Request</option>
                      <option value="other">Other</option>
                    </select>
                  </div>
                  
                  <div>
                    <label htmlFor="feedback" className="block text-sm font-medium text-gray-700 mb-1">Your Feedback</label>
                    <textarea
                      id="feedback"
                      name="feedback"
                      rows="4"
                      required
                      className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-primary focus:border-primary"
                      placeholder="Share your thoughts, ideas, or report issues..."
                    ></textarea>
                  </div>
                  
                  <div className="flex items-center">
                    <input 
                      id="subscribe" 
                      name="subscribe" 
                      type="checkbox" 
                      value="yes"
                      className="h-4 w-4 text-primary focus:ring-primary border-gray-300 rounded" 
                    />
                    <label htmlFor="subscribe" className="ml-2 block text-sm text-gray-700">
                      Keep me updated on new features
                    </label>
                  </div>
                  
                  <div className="text-xs text-gray-500 mb-4">
                    Your data is securely processed. We never share your information with third parties.
                  </div>
                  
                  <motion.button
                    type="submit"
                    className="w-full md:w-auto px-6 py-3 bg-primary text-white font-medium rounded-md shadow-md hover:bg-primary-dark transition-colors"
                    whileHover={{ scale: 1.03 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    Submit Feedback
                  </motion.button>
                </form>
              </div>
            </div>
          </motion.div>
        </section>
        
        {/* Animal articles section - replaced API info section */}
        <section id="articles" className="max-w-4xl mx-auto mt-12">
          <h2 className="text-2xl font-bold mb-6 text-center">Animal Knowledge Center</h2>
          
          <div className="space-y-8">
            {animalArticles.map(article => (
              <motion.article 
                key={article.id}
                className="bg-white rounded-xl shadow-card overflow-hidden"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
                viewport={{ once: true, margin: "-100px" }}
              >
                <div className="md:flex">
                  <div className="md:shrink-0 h-56 md:h-auto md:w-1/3">
                    <img 
                      className="h-full w-full object-cover"
                      src={article.imageUrl} 
                      alt={article.title}
                      loading="lazy"
                    />
                  </div>
                  <div className="p-6 md:w-2/3">
                    <h3 className="text-xl font-bold mb-3">{article.title}</h3>
                    <p className="text-gray-600">{article.content}</p>
                    <button className="mt-4 btn-outline">
                      Read More
                    </button>
                  </div>
                </div>
              </motion.article>
            ))}
          </div>
          
          <div className="text-center mt-8">
            <a href="/articles" className="btn-secondary inline-block">
              Explore Our Articles
            </a>
          </div>
        </section>
      </main>
      
      {/* Footer */}
      <Footer />
    </div>
  );
};

export default App; 